<template>
  <div class="modal-overlay" @click="closeModal" v-if="isOpen">
    <div class="modal" @click.stop>
      <div class="modal-header">
        <h2 class="modal-title">Carica Analisi in Blocco</h2>
        <button class="close-button" @click="closeModal" aria-label="Chiudi">
          &times;
        </button>
      </div>

      <div class="modal-body">
        <!-- Step 1: File Upload -->
        <div v-if="step === 'upload'" class="upload-section">
          <p class="upload-description">
            Carica un file Excel (.xlsx, .xls) o CSV contenente i dati delle aziende da analizzare.
          </p>

          <div
            class="drop-zone"
            :class="{ 'drop-zone--active': isDragging }"
            @dragover.prevent="handleDragOver"
            @dragleave="handleDragLeave"
            @drop.prevent="handleDrop"
            @click="triggerFileInput"
          >
            <input
              type="file"
              ref="fileInput"
              @change="handleFileSelect"
              accept=".csv,.xlsx,.xls"
              class="file-input"
            />
            <div class="drop-zone-content">
              <svg class="upload-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <polyline points="17,8 12,3 7,8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <line x1="12" y1="3" x2="12" y2="15" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <p class="drop-zone-text">
                Trascina il file qui o <span class="link">sfoglia</span>
              </p>
              <p class="drop-zone-hint">
                Formati supportati: CSV, XLSX, XLS (max 1000 aziende)
              </p>
            </div>
          </div>

          <div v-if="selectedFile" class="selected-file">
            <div class="file-info">
              <span class="file-name">{{ selectedFile.name }}</span>
              <span class="file-size">{{ formatFileSize(selectedFile.size) }}</span>
            </div>
            <button class="remove-file" @click="clearFile" aria-label="Rimuovi file">
              &times;
            </button>
          </div>

          <!-- Job Name -->
          <div class="form-group" v-if="selectedFile">
            <label for="job_name" class="form-label">Nome Job (opzionale)</label>
            <input
              type="text"
              id="job_name"
              v-model="jobName"
              class="form-input"
              :placeholder="selectedFile.name"
            />
          </div>

          <div class="format-guide">
            <h4>Formato file richiesto:</h4>
            <ul>
              <li><strong>Colonna obbligatoria:</strong> company_name (o: Company, Name, Brand)</li>
              <li><strong>Colonne opzionali:</strong> website, instagram, facebook, linkedin, sector</li>
            </ul>
          </div>
        </div>

        <!-- Step 2: Preview -->
        <div v-if="step === 'preview'" class="preview-section">
          <div class="preview-header">
            <h3>Anteprima Dati</h3>
            <p class="preview-summary">
              {{ previewData.valid_rows }} aziende valide su {{ previewData.total_rows }} righe
            </p>
          </div>

          <div v-if="previewData.warnings.length > 0" class="warnings-list">
            <h4>Avvisi:</h4>
            <ul>
              <li v-for="(warning, index) in previewData.warnings" :key="index">
                {{ warning }}
              </li>
            </ul>
          </div>

          <div v-if="previewData.errors.length > 0" class="errors-list">
            <h4>Errori:</h4>
            <ul>
              <li v-for="(error, index) in previewData.errors" :key="index">
                {{ error }}
              </li>
            </ul>
          </div>

          <div class="preview-table-container">
            <table class="preview-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Azienda</th>
                  <th>Website</th>
                  <th>Instagram</th>
                  <th>Settore</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(company, index) in previewCompanies" :key="index">
                  <td>{{ index + 1 }}</td>
                  <td>{{ company.company_name }}</td>
                  <td>{{ company.website || '-' }}</td>
                  <td>{{ company.instagram_url || '-' }}</td>
                  <td>{{ company.sector || '-' }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p v-if="previewData.companies.length > 10" class="preview-note">
            Mostrando le prime 10 aziende di {{ previewData.companies.length }}
          </p>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="loading-section">
          <div class="spinner"></div>
          <p>{{ loadingMessage }}</p>
        </div>

        <!-- Error State -->
        <div v-if="error" class="error-section">
          <p class="error-message">{{ error }}</p>
        </div>
      </div>

      <div class="modal-footer">
        <button type="button" @click="handleBack" class="button button-secondary">
          {{ step === 'preview' ? 'Indietro' : 'Annulla' }}
        </button>
        <button
          v-if="step === 'upload'"
          type="button"
          @click="handleUpload"
          class="button button-primary"
          :disabled="!selectedFile || loading"
        >
          {{ loading ? 'Caricamento...' : 'Carica' }}
        </button>
        <button
          v-if="step === 'preview'"
          type="button"
          @click="handleConfirm"
          class="button button-primary"
          :disabled="loading || previewData.valid_rows === 0"
        >
          {{ loading ? 'Creazione...' : `Avvia Analisi (${previewData.valid_rows})` }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { useBulkAnalysisStore } from '@/stores/bulkAnalysisStore';
import { useAuthStore } from '@/stores/authStore';

export default {
  name: 'BulkUploadModal',
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      step: 'upload', // 'upload' | 'preview'
      selectedFile: null,
      jobName: '',
      isDragging: false,
      loading: false,
      loadingMessage: '',
      error: null,
      previewData: {
        success: false,
        companies: [],
        total_rows: 0,
        valid_rows: 0,
        errors: [],
        warnings: [],
      },
    };
  },
  computed: {
    previewCompanies() {
      return this.previewData.companies.slice(0, 10);
    },
  },
  methods: {
    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        this.validateAndSetFile(file);
      }
    },

    handleDragOver(event) {
      event.preventDefault();
      this.isDragging = true;
    },

    handleDragLeave() {
      this.isDragging = false;
    },

    handleDrop(event) {
      this.isDragging = false;
      const file = event.dataTransfer.files[0];
      if (file) {
        this.validateAndSetFile(file);
      }
    },

    validateAndSetFile(file) {
      const validTypes = [
        'text/csv',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      ];
      const validExtensions = ['.csv', '.xls', '.xlsx'];

      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

      if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
        this.error = 'Formato file non supportato. Usa CSV, XLS o XLSX.';
        return;
      }

      // Max file size: 10MB
      if (file.size > 10 * 1024 * 1024) {
        this.error = 'Il file è troppo grande. Dimensione massima: 10MB.';
        return;
      }

      this.selectedFile = file;
      this.error = null;
    },

    clearFile() {
      this.selectedFile = null;
      this.jobName = '';
      this.$refs.fileInput.value = '';
    },

    formatFileSize(bytes) {
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    },

    async handleUpload() {
      if (!this.selectedFile) return;

      const authStore = useAuthStore();
      const userId = authStore.userId;

      if (!userId) {
        this.error = 'Devi essere autenticato per caricare analisi.';
        return;
      }

      this.loading = true;
      this.loadingMessage = 'Analisi del file in corso...';
      this.error = null;

      try {
        const bulkStore = useBulkAnalysisStore();
        const result = await bulkStore.uploadBulkJob(
          this.selectedFile,
          userId,
          this.jobName || this.selectedFile.name
        );

        // If upload was successful, emit and close
        this.$emit('created', result);
        this.closeModal();
      } catch (error) {
        this.error = error.message;
      } finally {
        this.loading = false;
      }
    },

    handleConfirm() {
      // This is kept for future preview functionality
      this.handleUpload();
    },

    handleBack() {
      if (this.step === 'preview') {
        this.step = 'upload';
        this.previewData = {
          success: false,
          companies: [],
          total_rows: 0,
          valid_rows: 0,
          errors: [],
          warnings: [],
        };
      } else {
        this.closeModal();
      }
    },

    resetForm() {
      this.step = 'upload';
      this.selectedFile = null;
      this.jobName = '';
      this.isDragging = false;
      this.loading = false;
      this.error = null;
      this.previewData = {
        success: false,
        companies: [],
        total_rows: 0,
        valid_rows: 0,
        errors: [],
        warnings: [],
      };
    },

    closeModal() {
      this.resetForm();
      this.$emit('close');
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;
}

.modal {
  background: white;
  width: 100%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 2px solid #2e4eed;
}

.modal-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.close-button {
  background: none;
  border: none;
  font-size: 2rem;
  line-height: 1;
  cursor: pointer;
  color: #1a1a1a;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s ease;
}

.close-button:hover {
  opacity: 0.7;
}

.modal-body {
  padding: 1.5rem;
  flex: 1;
  overflow-y: auto;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 1px solid #e5e7eb;
}

/* Upload Section */
.upload-description {
  color: #6b7280;
  margin-bottom: 1.5rem;
}

.drop-zone {
  border: 2px dashed #2e4eed;
  border-radius: 8px;
  padding: 3rem 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #f8fafc;
}

.drop-zone:hover,
.drop-zone--active {
  background: rgba(46, 78, 237, 0.05);
  border-color: #1e3ed8;
}

.file-input {
  display: none;
}

.upload-icon {
  width: 48px;
  height: 48px;
  color: #2e4eed;
  margin-bottom: 1rem;
}

.drop-zone-text {
  font-size: 1.125rem;
  color: #1a1a1a;
  margin-bottom: 0.5rem;
}

.drop-zone-text .link {
  color: #2e4eed;
  text-decoration: underline;
}

.drop-zone-hint {
  font-size: 0.875rem;
  color: #6b7280;
}

.selected-file {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f0f9ff;
  border: 1px solid #2e4eed;
  margin-top: 1rem;
}

.file-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.file-name {
  font-weight: 500;
  color: #1a1a1a;
}

.file-size {
  font-size: 0.875rem;
  color: #6b7280;
}

.remove-file {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #6b7280;
  cursor: pointer;
  padding: 0.25rem;
  line-height: 1;
}

.remove-file:hover {
  color: #ef4444;
}

.form-group {
  margin-top: 1.5rem;
}

.form-label {
  display: block;
  font-size: 1rem;
  font-weight: 500;
  color: #1a1a1a;
  margin-bottom: 0.5rem;
}

.form-input {
  width: 100%;
  height: 3rem;
  padding: 0 1rem;
  border: 2px solid #2e4eed;
  background: white;
  font-size: 1rem;
  transition: all 0.3s ease;
}

.form-input:focus {
  outline: none;
  border-color: #56bcee;
  box-shadow: 0 0 0 2px rgba(86, 188, 238, 0.2);
}

.format-guide {
  margin-top: 1.5rem;
  padding: 1rem;
  background: #f8fafc;
  border-left: 3px solid #2e4eed;
}

.format-guide h4 {
  margin: 0 0 0.5rem 0;
  font-size: 0.875rem;
  color: #1a1a1a;
}

.format-guide ul {
  margin: 0;
  padding-left: 1.25rem;
  font-size: 0.875rem;
  color: #6b7280;
}

.format-guide li {
  margin-bottom: 0.25rem;
}

/* Preview Section */
.preview-header {
  margin-bottom: 1rem;
}

.preview-header h3 {
  margin: 0 0 0.5rem 0;
}

.preview-summary {
  color: #2e4eed;
  font-weight: 500;
}

.warnings-list,
.errors-list {
  padding: 1rem;
  margin-bottom: 1rem;
  border-radius: 4px;
}

.warnings-list {
  background: #fef3c7;
  border-left: 3px solid #f59e0b;
}

.errors-list {
  background: #fee2e2;
  border-left: 3px solid #ef4444;
}

.warnings-list h4,
.errors-list h4 {
  margin: 0 0 0.5rem 0;
  font-size: 0.875rem;
}

.warnings-list ul,
.errors-list ul {
  margin: 0;
  padding-left: 1.25rem;
  font-size: 0.875rem;
}

.preview-table-container {
  overflow-x: auto;
  border: 1px solid #e5e7eb;
}

.preview-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.preview-table th,
.preview-table td {
  padding: 0.75rem;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
}

.preview-table th {
  background: #f8fafc;
  font-weight: 600;
}

.preview-table td {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.preview-note {
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: #6b7280;
  font-style: italic;
}

/* Loading & Error States */
.loading-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e5e7eb;
  border-top-color: #2e4eed;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.error-section {
  padding: 1rem;
  background: #fee2e2;
  border-left: 3px solid #ef4444;
}

.error-message {
  color: #dc2626;
  margin: 0;
}

/* Buttons */
.button {
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 500;
  border: 2px solid;
  cursor: pointer;
  transition: all 0.3s ease;
}

.button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.button-primary {
  background: #2e4eed;
  border-color: #2e4eed;
  color: white;
}

.button-primary:hover:not(:disabled) {
  background: #1e3ed8;
  border-color: #1e3ed8;
}

.button-secondary {
  background: white;
  border-color: #2e4eed;
  color: #2e4eed;
}

.button-secondary:hover {
  background: rgba(46, 78, 237, 0.05);
}

@media (max-width: 768px) {
  .modal {
    max-width: 100%;
    max-height: 100vh;
    height: 100%;
  }

  .modal-header {
    padding: 1rem;
  }

  .modal-title {
    font-size: 1.25rem;
  }

  .modal-body {
    padding: 1rem;
  }

  .modal-footer {
    padding: 1rem;
    flex-direction: column;
  }

  .button {
    width: 100%;
  }

  .drop-zone {
    padding: 2rem 1rem;
  }
}
</style>
